import React, { useMemo, useCallback, useContext } from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import NdaStatusBadge from './NdaStatusBadge';
import { NDA_STATUSES, NdaStatus } from 'models/Nda';
import MessagingContext from '../../providers/Messaging/Messaging.context';
import { getUserFullName, isNdaNotSent } from 'utils';
import AddNdaButton from 'components/AddNdaButton';
import { Listing } from 'models/Listing';
import LinkToIcon from 'icons/LinkTo.icon';
import useNdaStatus, { LeadNdaProps } from 'hooks/useNdaStatus';
import AutomaticMessageModal from 'components/AutomaticMessageModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';

import './NdaSigningProgress.styles.scss';
import './NdaSigningProgress.styles.responsive.scss';

type NdaSigningProgressProps = {
  className?: string;
};

const NdaSigningProgress: React.FC<NdaSigningProgressProps> = (props) => {
  const { className } = props;

  const {
    interlocutor,
    currentSession: {
      leadId,
      signingStatus,
      envelopeId,
      sentDate,
      signedDate,
      manuallySetNDAStatus,
      rejectionReason,
      listing: { envelopeTemplateId, _id },
    },
    setCurrentSession,
  } = useContext(MessagingContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const interlocutorFullName = useMemo(
    () => getUserFullName(interlocutor?.firstName, interlocutor?.lastName),
    [interlocutor?.firstName, interlocutor?.lastName],
  );

  const { t } = useTranslation();

  const updateNdaProps = useCallback(
    (leadNdaProps: LeadNdaProps) => {
      setCurrentSession((old) => ({
        ...old,
        ...leadNdaProps,
      }));
    },
    [setCurrentSession],
  );

  const { isLoading, getAndOpenNdaLink, sendNda, resendNda } = useNdaStatus(
    leadId,
    envelopeId,
    updateNdaProps,
  );

  const classes = classNames(
    'nda-signing-progress',
    { 'nda-signing-progress--loading': isLoading },
    className,
  );

  const CONTENT: Record<NdaStatus, JSX.Element> = useMemo(() => {
    const buttonProps: any = {
      icon: <LinkToIcon />,
      iconPosition: 'right',
      className: 'as-link',
      disabled: isLoading,
    };

    return {
      [NDA_STATUSES.NOT_SENT]: (
        <>
          <Button {...buttonProps} onClick={modal?.open}>
            {t('sendCATo', { recepient: interlocutorFullName })}
          </Button>

          <AutomaticMessageModal
            ref={modalRef}
            leadFirstName={interlocutor?.firstName}
            leadLastName={interlocutor?.lastName}
            onSendNda={(message) => {
              modal?.close();
              sendNda(message);
            }}
          />
        </>
      ),
      [NDA_STATUSES.SENT]: (
        <>
          <NdaStatusBadge status={NDA_STATUSES.SENT} sentDate={sentDate} />
          {envelopeTemplateId && (
            <Button {...buttonProps} onClick={resendNda}>
              {t('resendNDA')}
            </Button>
          )}
        </>
      ),
      [NDA_STATUSES.SIGNED]: (
        <>
          <NdaStatusBadge
            status={NDA_STATUSES.SIGNED}
            signedDate={signedDate}
          />
          {!manuallySetNDAStatus && (
            <div>
              <Button {...buttonProps} onClick={getAndOpenNdaLink}>
                {t('previewSignedCA')}
              </Button>
            </div>
          )}
        </>
      ),
      [NDA_STATUSES.REJECTED]: (
        <NdaStatusBadge
          status={NDA_STATUSES.REJECTED}
          rejectionReason={rejectionReason}
        />
      ),
      [NDA_STATUSES.COUNTER_SIGNATURE_PENDING]: (
        <NdaStatusBadge
          status={NDA_STATUSES.COUNTER_SIGNATURE_PENDING}
          signedDate={signedDate}
        />
      ),
    };
  }, [
    modal,
    modalRef,
    isLoading,
    manuallySetNDAStatus,
    interlocutorFullName,
    interlocutor?.firstName,
    interlocutor?.lastName,
    sentDate,
    signedDate,
    envelopeTemplateId,
    rejectionReason,
    t,
    sendNda,
    resendNda,
    getAndOpenNdaLink,
  ]);

  const setListing = useCallback(
    (listing: Listing) => {
      setCurrentSession((prev) => ({
        ...prev,
        listing: { ...listing, dealRoom: prev.listing.dealRoom },
      }));
    },
    [setCurrentSession],
  );

  if (!isNdaNotSent(signingStatus))
    return (
      <div className={classes}>
        <p className="title">{t('nonDisclosureAgreement')}</p>
        {CONTENT[signingStatus]}
      </div>
    );

  if (!envelopeTemplateId)
    return (
      <div className="nda-signing-progress--empty">
        <p className="title">{t('nonDisclosureAgreement')}</p>
        <p>{t('youDontHaveNda')}</p>

        <AddNdaButton
          listingId={_id}
          envelopeTemplateId={envelopeTemplateId}
          setListing={setListing}
        />
      </div>
    );

  return (
    <div className={classes}>
      <p className="title">{t('nonDisclosureAgreement')}</p>
      {CONTENT[signingStatus]}
    </div>
  );
};

export default NdaSigningProgress;
